import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EyeOff, ImageUp, RotateCcw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { centeredCrop, hasAspect, ImageCropModal, loadImage, renderCrop, type Crop, type LoadedImage } from './image-crop';
import { SignInWelcome } from './sign-in-welcome';
import type { SignInSettingsData } from './SignInSettings';
import { useToast } from './toast';
import { Button, Card, CardHeader, cn, Field, Input, Textarea } from './ui';

/**
 * The panel beside the sign-in form is about 4:5 on common laptop and desktop screens (it's 52% of the window's width
 * and all of its height). Wider or narrower windows trim the image's edges a little.
 */
const ASPECT = 4 / 5;
const IMAGE_WIDTH = 1600;
/** Narrower images may look blurry on a large display, so they open the crop window with a warning. */
const MIN_SHARP_WIDTH = 1000;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_HEADLINE = 120;
const MAX_MESSAGE = 300;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
/** The preview draws the panel at this size, then scales it down. */
const PANEL = { width: 760, height: 950 };
const PREVIEW_WIDTH = 256;

function readBlob(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

type SignInText = { headline: string | null; message: string | null };

/** A school's sign-in page: the background image and welcome text beside the form, with a preview. */
export function SignInPageSettings({ settings }: { settings: SignInSettingsData }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const savedHeadline = settings.signInHeadline ?? settings.defaultSignInHeadline;
  const savedMessage = settings.signInMessage ?? settings.defaultSignInMessage;
  const [headline, setHeadline] = useState(savedHeadline);
  const [message, setMessage] = useState(savedMessage);
  useEffect(() => setHeadline(savedHeadline), [savedHeadline]);
  useEffect(() => setMessage(savedMessage), [savedMessage]);
  const [cropping, setCropping] = useState<LoadedImage | null>(null);
  const [preparing, setPreparing] = useState(false);
  const closeCrop = useCallback(() => setCropping(null), []);
  useEffect(() => {
    if (!cropping) return;
    return () => URL.revokeObjectURL(cropping.url);
  }, [cropping]);

  const refresh = (data: SignInSettingsData) => {
    queryClient.setQueryData(['tenant-sign-in'], data);
    void queryClient.invalidateQueries({ queryKey: ['public-school'] });
  };
  const onError = (err: unknown) => toast.error(errorMessage(err));

  const uploadImage = useMutation({
    mutationFn: async (image: Blob) => api.put<SignInSettingsData>('/tenant/sign-in-image', { image: await readBlob(image) }),
    onSuccess: (data) => {
      refresh(data);
      setCropping(null);
      toast.success('Background image saved');
    },
    onError,
  });
  const removeImage = useMutation({
    mutationFn: () => api.delete<SignInSettingsData>('/tenant/sign-in-image'),
    onSuccess: (data) => {
      refresh(data);
      toast.success('Background image removed');
    },
    onError,
  });
  const saveText = useMutation({
    mutationFn: (text: SignInText) => api.put<SignInSettingsData>('/tenant/sign-in-text', text),
    onSuccess: (data) => {
      refresh(data);
      toast.success('Sign-in page text saved');
    },
    onError,
  });

  const busy = preparing || uploadImage.isPending;

  const save = async (source: LoadedImage, crop: Crop) => {
    setPreparing(true);
    try {
      uploadImage.mutate(await renderCrop(source.image, crop, IMAGE_WIDTH, MAX_IMAGE_BYTES));
    } catch (err) {
      onError(err);
    } finally {
      setPreparing(false);
    }
  };

  const choose = async (file: File) => {
    if (!ACCEPTED.includes(file.type)) {
      toast.error('Choose a PNG, JPEG, or WebP image.');
      return;
    }
    let source: LoadedImage;
    try {
      source = await loadImage(file);
    } catch {
      toast.error(`${file.name} couldn’t be opened. Choose a PNG, JPEG, or WebP image.`);
      return;
    }
    // Already the panel's shape and sharp enough: it only needs resizing. Anything else is cropped first.
    if (hasAspect(source.image, ASPECT) && source.image.naturalWidth >= MIN_SHARP_WIDTH) {
      await save(source, centeredCrop(source.image, ASPECT));
      URL.revokeObjectURL(source.url);
    } else {
      setCropping(source);
    }
  };

  const textChanged = headline.trim() !== savedHeadline || message.trim() !== savedMessage;
  const usesEdvoraText = settings.signInHeadline === null && settings.signInMessage === null;
  const textRemoved = savedHeadline === '' && savedMessage === '';
  const previewScale = PREVIEW_WIDTH / PANEL.width;

  return (
    <Card>
      <CardHeader title="Sign-in page" description="The background image and welcome text beside the sign-in form." />
      <div className="flex flex-col gap-8 p-5 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-8">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Background image</h3>
            <p className="text-sm text-slate-600">
              A photo of your campus works well. The page shows it in a 4:5 portrait shape, ideally 1600 × 2000 pixels. If your image is a different
              shape, you’ll choose the part to show.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <label
                className={cn(
                  'inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50',
                  busy && 'pointer-events-none opacity-60',
                )}
              >
                <ImageUp className="size-4" />
                {busy && !cropping ? 'Uploading…' : settings.signInImageUrl ? 'Replace image' : 'Upload image'}
                <input
                  type="file"
                  accept={ACCEPTED.join(',')}
                  className="sr-only"
                  disabled={busy}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void choose(file);
                  }}
                />
              </label>
              {settings.signInImageUrl && (
                <Button size="sm" variant="ghost" icon={<Trash2 />} loading={removeImage.isPending} onClick={() => removeImage.mutate()}>
                  Remove image
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500">PNG, JPEG, or WebP. Without an image, the page uses your school color.</p>
          </section>

          <section className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Welcome text</h3>
            <Field label="Headline" hint="Leave it empty to hide it.">
              <Input value={headline} maxLength={MAX_HEADLINE} onChange={(event) => setHeadline(event.target.value)} />
            </Field>
            <Field label="Message" hint="Leave it empty to hide it.">
              <Textarea rows={3} value={message} maxLength={MAX_MESSAGE} onChange={(event) => setMessage(event.target.value)} />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={!textChanged}
                loading={saveText.isPending}
                onClick={() => saveText.mutate({ headline: headline.trim(), message: message.trim() })}
              >
                Save text
              </Button>
              {!textRemoved && (
                <Button variant="ghost" icon={<EyeOff />} disabled={saveText.isPending} onClick={() => saveText.mutate({ headline: '', message: '' })}>
                  Remove text
                </Button>
              )}
              {!usesEdvoraText && (
                <Button variant="ghost" icon={<RotateCcw />} disabled={saveText.isPending} onClick={() => saveText.mutate({ headline: null, message: null })}>
                  Use Edvora’s text
                </Button>
              )}
            </div>
          </section>
        </div>

        <div className="shrink-0">
          <p className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">Preview</p>
          <div
            className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm"
            style={{ width: PREVIEW_WIDTH, height: PANEL.height * previewScale }}
            aria-hidden
          >
            <div className="pointer-events-none absolute top-0 left-0 origin-top-left" style={{ width: PANEL.width, height: PANEL.height, transform: `scale(${previewScale})` }}>
              <SignInWelcome
                className="size-full"
                name={settings.name}
                logoUrl={settings.logoUrl}
                imageUrl={settings.signInImageUrl}
                headline={headline.trim()}
                message={message.trim()}
              />
            </div>
          </div>
          <p className="mt-2 max-w-64 text-xs text-slate-500">Shown beside the sign-in form on screens at least 1024 pixels wide.</p>
        </div>
      </div>

      {cropping && (
        <ImageCropModal
          source={cropping}
          aspect={ASPECT}
          title="Crop the background image"
          description="The sign-in page shows what’s inside the frame. Drag the image and zoom to choose."
          maxWidth={IMAGE_WIDTH}
          minSharpWidth={MIN_SHARP_WIDTH}
          saving={busy}
          onCancel={closeCrop}
          onConfirm={(crop) => void save(cropping, crop)}
        />
      )}
    </Card>
  );
}
